import React, { useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faClose,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const CustomModal = ({
  open,
  onClose,
  title,
  children,
  showBackButton = false,
  onBack,
  contentClass = "",
  titleClass = "",
  headerClass = "border-b border-gray-200",
  bodyClass = "",
  variant = "default", // 'default' | 'primary'
  position = "center", // 'center' | 'top'
  zIndex = 9999,
  disablePortal = false,
}) => {
  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        onClose && onClose();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!open) return null;

  const isPrimary = variant === "primary";

  const baseContent = isPrimary ? "bg-white dark:bg-[#252739] text-white dark:text-dark-text" : "bg-white dark:bg-[#252739]";
  const mergedContentClass = `${baseContent} w-[80%] rounded-2xl shadow-xl md:max-w-lg md:max-h-[90vh] relative flex flex-col animate-fadeIn ${contentClass}`;

  const defaultHeader = isPrimary
    ? "border-b border-white/10"
    : "border-b border-gray-200 dark:border-dark-border";
  const mergedHeaderClass = `${defaultHeader} flex items-center justify-between p-4 md:p-6 flex-shrink-0 ${headerClass}`;

  const actionBtnBase = isPrimary
    ? "w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-white/25 text-white rounded-full transition-colors"
    : "w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-[#252739] rounded-full text-gray-500 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-[#2f3050] transition-colors";

  const titleBase = isPrimary
    ? "text-lg font-semibold text-black dark:text-dark-text"
    : "text-lg font-semibold text-gray-800 dark:text-dark-text";
  const mergedTitleClass = `text-black dark:text-dark-text ${titleBase} ${titleClass}`;
  const containerClasses = [
    "fixed text-black inset-0 w-full h-full flex",
    position === "top"
      ? "items-start justify-center pt-6 md:pt-10"
      : "items-center justify-center",
    "bg-black/50 md:bg-black/40 p-0 md:p-4",
  ].join(" ");

  const modalElement = (
    <div
      className={containerClasses}
      style={{ zIndex }}
      onClick={() => onClose && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className={mergedContentClass} onClick={(e) => e.stopPropagation()}>
        <div className={mergedHeaderClass}>
          <div className="flex items-center gap-3">
            {showBackButton && onBack && (
              <button className={actionBtnBase} onClick={onBack}>
                <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
              </button>
            )}
            <h2 className={mergedTitleClass}>{title}</h2>
          </div>
          <button
            className={actionBtnBase}
            onClick={(e) => {
              e.stopPropagation();
              onClose && onClose();
            }}
          >
            <FontAwesomeIcon
              icon={faClose}
              color="red"
              className="text-sm p-2 bg-red-500 w-3 h-3 flex items-center justify-center rounded-full text-white"
            />
          </button>
        </div>
        <div
          className={`overflow-y-auto flex-1 p-4 md:p-6 ${
            isPrimary ? "text-white" : "text-gray-900 dark:text-dark-text"
          }  ${bodyClass}`}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (disablePortal) return modalElement;
  const mountNode = typeof document !== "undefined" ? document.body : null;
  return mountNode
    ? ReactDOM.createPortal(modalElement, mountNode)
    : modalElement;
};

export default CustomModal;
